import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoRectangularManwayJSON {
  type: GeckoFeatureModelType.RectangularManway
  name: string
  feature_geometry: {
    x: number
    y: number
    width: number
    height: number
  }
  surface_specs: {
    thickness: number | undefined
  }
}

export const NewGeckoRectangularManwayJSON: GeckoRectangularManwayJSON = {
  type: GeckoFeatureModelType.RectangularManway,
  name: 'New Rectangular Manway',
  feature_geometry: {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  },
  surface_specs: {
    thickness: undefined,
  },
}

export class GeckoRectangularManway extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.RectangularManway
  readonly geometry: GeckoComponentCoords
  readonly width: GeckoMeasurement
  readonly height: GeckoMeasurement
  readonly thickness: GeckoMeasurement | undefined

  private constructor(
    id: string,
    name: string,
    geometry: GeckoComponentCoords,
    width: GeckoMeasurement,
    height: GeckoMeasurement,
    thickness: GeckoMeasurement | undefined
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
    this.width = width
    this.height = height
    this.thickness = thickness
  }

  public static CreateFromJSON(
    componentID: string,
    manwayID: string,
    manwayJSON: GeckoRectangularManwayJSON
  ): GeckoRectangularManway {
    const featureGeometryData = manwayJSON.feature_geometry as Record<
      string,
      number
    >
    const manwayGeometry = GeckoComponentCoords.CreateFromValues(
      componentID,
      featureGeometryData.x,
      GeckoMeasurementUnit.Meters,
      featureGeometryData.y,
      GeckoMeasurementUnit.Meters
    )
    const manwayWidth = GeckoMeasurement.CreateFromValue(
      featureGeometryData.width as number,
      GeckoMeasurementUnit.Meters
    )
    const manwayHeight = GeckoMeasurement.CreateFromValue(
      featureGeometryData.height as number,
      GeckoMeasurementUnit.Meters
    )

    const surfaceSpecs = manwayJSON.surface_specs as Record<string, number>
    const manwayThickness =
      surfaceSpecs.thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.thickness as number,
            GeckoMeasurementUnit.Meters
          )
        : undefined

    const manwayName = manwayJSON.name as string

    return new GeckoRectangularManway(
      manwayID,
      manwayName,
      manwayGeometry,
      manwayWidth,
      manwayHeight,
      manwayThickness
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoRectangularManwayJSON>
  ): Map<string, GeckoRectangularManway> {
    return Object.keys(recordsJSON).reduce((resultObj, manwayID) => {
      resultObj.set(
        manwayID,
        GeckoRectangularManway.CreateFromJSON(
          componentID,
          manwayID,
          recordsJSON[manwayID]
        )
      )
      return resultObj
    }, new Map<string, GeckoRectangularManway>())
  }

  asJSON(): GeckoRectangularManwayJSON {
    return generateRectangularManway(
      this.name,
      this.geometry.x,
      this.geometry.y,
      this.width,
      this.height,
      this.thickness
    )
  }
}

export function generateRectangularManway(
  name: string,
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  width: GeckoMeasurement,
  height: GeckoMeasurement,
  thickness: GeckoMeasurement | undefined
): GeckoRectangularManwayJSON {
  const manwayJSON: GeckoRectangularManwayJSON = {
    type: GeckoFeatureModelType.RectangularManway,
    name: name,
    feature_geometry: {
      x: x.toNumber(GeckoMeasurementUnit.Meters),
      y: y.toNumber(GeckoMeasurementUnit.Meters),
      width: width.toNumber(GeckoMeasurementUnit.Meters),
      height: height.toNumber(GeckoMeasurementUnit.Meters),
    },
    surface_specs: {
      thickness:
        thickness !== undefined
          ? thickness.toNumber(GeckoMeasurementUnit.Meters)
          : undefined,
    },
  }

  return manwayJSON
}

export const RectangularManwayFormFields = (
  manwayJSON: GeckoRectangularManwayJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Name',
      value: manwayJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: manwayJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: manwayJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'width',
      label: 'Width',
      value: manwayJSON.feature_geometry.width,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'height',
      label: 'Height',
      value: manwayJSON.feature_geometry.height,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'thickness',
      label: 'Thickness',
      value: manwayJSON.surface_specs.thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const RectangularManwayJSONFromFormFields = (
  formFields: FormField[]
) => {
  const manwayThickness = getFormFieldByDataKey(formFields, 'thickness')

  return generateRectangularManway(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'width')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'height')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    manwayThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          manwayThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined
  )
}

export const RectangularManwayFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.Manway,
  title: 'Rectangular Manways',
  singularName: 'Rectangular Manway',
  fields: ['x', 'y', 'width', 'height', 'thickness'],
}
